import React, { useState } from "react";
import Accordion from "react-bootstrap/Accordion";
import { useAccordionButton } from "react-bootstrap/AccordionButton";
import Typography from "ui/Typography";
import ExpandIcon from "ui/Icons/Expand";
import CollapseIcon from "ui/Icons/UpChevron";
import Info from "ui/Icons/Info";
import {
  $ExpandHeadingWrapper,
  $ExpandButtonContainer,
  $ExpandButton,
  $ContentContainer,
  $InlineTypography,
  $IconContainer,
  $ExpandPillButton,
} from "./styled-components";
import Tooltip from "ui/Tooltip";

interface ExpandButtonProps {
  children: React.ReactNode;
  eventKey: string;
  onClick: (expanded: boolean) => void;
  expanded: boolean;
  analyticsName: string;
  variant?: "link" | "pill";
}

function ExpandButton({
  children,
  eventKey,
  onClick,
  expanded,
  analyticsName,
  variant,
}: ExpandButtonProps) {
  const expandOnClick = useAccordionButton(eventKey, () => onClick(!expanded));
  if (variant === "pill") {
    return (
      <$ExpandPillButton
        analyticsName={analyticsName}
        onClick={expandOnClick}
        aria-expanded={expanded}
        requestInProgress={false}
        variant="secondary"
      >
        {children}
      </$ExpandPillButton>
    );
  }
  return (
    <$ExpandButton
      variant={variant}
      analyticsName={analyticsName}
      onClick={expandOnClick}
      aria-expanded={expanded}
      requestInProgress={false}
    >
      {children}
    </$ExpandButton>
  );
}

interface CollapsibleContainerProps {
  numOfPreviewCards?: number;
  /**
   * String describing what is being shown/hidden. This will be used in text for
   * screen readers in the form of `Show more ${contentType}`.
   */
  contentType: string;
  content?: React.ReactNodeArray;
  headingText?: string;
  tooltip?: string;
  expandAnalyticsName: string;
  buttonVariant?: "link" | "pill";
  buttonText?: string;
  initiallyExpanded?: boolean;
  onClick?: (expanded: boolean) => void;
  onEntered?: (node: HTMLElement) => void;
}

function CollapsibleContainer({
  numOfPreviewCards,
  contentType,
  content,
  headingText,
  tooltip,
  expandAnalyticsName,
  buttonVariant = "link",
  buttonText,
  initiallyExpanded = false,
  onClick,
  onEntered,
}: CollapsibleContainerProps) {
  const [expanded, setExpanded] = useState(initiallyExpanded);
  if (!content) {
    return null;
  }
  const totalNumOfContentCards = content.length;
  const isExpandable =
    numOfPreviewCards !== undefined &&
    totalNumOfContentCards > numOfPreviewCards;
  let previewContent = null;
  let remainingContent = null;
  if (isExpandable) {
    previewContent = content.slice(0, numOfPreviewCards);
    remainingContent = content.slice(numOfPreviewCards, content.length);
  }
  // required prop for Accordion.Collapse and needed for useAccordionToggle()
  const eventKey = "0";

  const hasHeader = !!headingText || !!tooltip;

  return (
    <Accordion
      data-testid="collapsible-container"
      defaultActiveKey={initiallyExpanded ? eventKey : undefined}
    >
      <section>
        {hasHeader && (
          <$ExpandHeadingWrapper>
            {headingText && (
              <$InlineTypography size="h3">{headingText}</$InlineTypography>
            )}
            {tooltip && (
              <Tooltip label={tooltip} placement="bottom">
                {(overlayProps) => (
                  <$IconContainer {...overlayProps} tabIndex={0}>
                    <Info
                      role="img"
                      color="ColorSneaker300"
                      title="More information about this section"
                    />
                  </$IconContainer>
                )}
              </Tooltip>
            )}
          </$ExpandHeadingWrapper>
        )}
        {isExpandable ? (
          <>
            <$ContentContainer>{previewContent}</$ContentContainer>
            {/* Setting the timeout prop here to mitigate a performance issue when expanding and collapsing the content */}
            <Accordion.Collapse
              onEntered={onEntered}
              eventKey={eventKey}
              timeout={0}
            >
              <$ContentContainer>{remainingContent}</$ContentContainer>
            </Accordion.Collapse>
            <$ContentContainer>
              <$ExpandButtonContainer>
                <ExpandButton
                  onClick={(expanded) => {
                    setExpanded(expanded);
                    onClick && onClick(expanded);
                  }}
                  eventKey={eventKey}
                  expanded={expanded}
                  analyticsName={expandAnalyticsName}
                  variant={buttonVariant}
                >
                  {expanded ? (
                    <Typography size="bodySerif" as="span">
                      <span aria-hidden>
                        {buttonText ? buttonText : "Show less"}
                      </span>
                      <span className="visually-hidden">{`Show fewer ${contentType}`}</span>
                      <CollapseIcon role="presentation" color="ColorSpace500" />
                    </Typography>
                  ) : (
                    <Typography size="bodySerif" as="span">
                      <span aria-hidden>
                        {buttonText ? buttonText : "Show more"}
                      </span>
                      <span className="visually-hidden">{`Show more ${contentType}`}</span>
                      <ExpandIcon role="presentation" color="ColorSpace500" />
                    </Typography>
                  )}
                </ExpandButton>
              </$ExpandButtonContainer>
            </$ContentContainer>
          </>
        ) : (
          <$ContentContainer>{content}</$ContentContainer>
        )}
      </section>
    </Accordion>
  );
}

export default CollapsibleContainer;
